'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { useForm, type UseFormRegister } from 'react-hook-form'
import { track } from '@/lib/analytics'
import { hasLink, withLinks } from '@/lib/editor-links'
import { buildFormSchema, defaultValuesFor } from '@/lib/form-schema'
import { CMS_ORIGIN } from '@/lib/site-config'
import type { Confirmation, FormField, FormObject } from '@/lib/types'

/**
 * The contact form, built from the schema the CMS serves.
 *
 * The fields, their validation and the endpoint all come from the panel, so
 * adding a question is an editorial act rather than a deploy. What this file
 * owns is the design: the underlined inputs, the accent on focus, and the one
 * line of status beside the button.
 *
 * Validation runs twice on purpose. Here, so a mistake is caught before a
 * request; and again on the server, because a browser is not a place to
 * enforce anything.
 */
export function ContactForm({ form }: { form: FormObject }) {
    const [status, setStatus] = useState('')
    const [failed, setFailed] = useState(false)
    // When this form reached the browser. The API compares it against the
    // form's `min_seconds` — a submission that arrives faster than a person
    // could type is not one.
    const renderedAt = useRef(new Date().toISOString())

    const schema = buildFormSchema(form.fields)

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaultValuesFor(form.fields),
    })

    // Filled in only by something that fills in every field it finds.
    const honeypotValue = useRef('')
    // A form is *started* once, at the first field someone touches. Pushing it
    // per focus would count someone tabbing back to correct a typo as a second
    // visitor thinking about writing.
    const started = useRef(false)

    function onFocus() {
        if (started.current) {
            return
        }

        started.current = true
        track({ event: 'form_start', form_name: form.slug })
    }

    /*
     * What the browser refused to send. The first offending field is enough:
     * a funnel wants to know *where* people give up, and the message itself is
     * the CMS's copy, which belongs in the panel rather than in a report.
     */
    function onInvalid(invalid: Record<string, unknown>) {
        track({
            event: 'form_error',
            form_name: form.slug,
            error_type: 'validation',
            error_field: Object.keys(invalid)[0],
        })
    }

    async function submit(values: Record<string, unknown>) {
        setFailed(false)
        setStatus('Versturen…')
        track({ event: 'form_submit', form_name: form.slug })

        const honeypot = form.honeypot === null ? {} : { [form.honeypot]: honeypotValue.current }

        try {
            // The envelope the API asks for: answers under `data`, everything
            // about the *submission* under `meta`. A flat body is a 422.
            const response = await fetch(new URL(form.endpoint, CMS_ORIGIN), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    data: values,
                    meta: { rendered_at: renderedAt.current, ...honeypot },
                }),
            })

            if (!response.ok) {
                track({
                    event: 'form_error',
                    form_name: form.slug,
                    error_type: response.status === 429 ? 'rate_limit' : 'server',
                })
                setFailed(true)
                setStatus(
                    response.status === 429
                        ? 'Even wachten — er zijn te veel berichten verstuurd.'
                        : 'Versturen lukte niet. Mail gerust rechtstreeks.',
                )

                return
            }

            // The conversion, pushed before the confirmation is read: a
            // redirect confirmation replaces the document, and an event queued
            // after that navigation is an event that never leaves.
            track({ event: 'generate_lead', form_name: form.slug })

            const body: { data?: { confirmation?: Confirmation } } = await response.json()
            const confirmation = body.data?.confirmation

            if (confirmation !== undefined && confirmation.type === 'redirect') {
                window.location.href = confirmation.url

                return
            }

            reset()
            setStatus(
                confirmation !== undefined && confirmation.type === 'text'
                    ? confirmation.message
                    : 'Bedankt, ik neem snel contact op.',
            )
        } catch {
            track({ event: 'form_error', form_name: form.slug, error_type: 'network' })
            setFailed(true)
            setStatus('Versturen lukte niet. Mail gerust rechtstreeks.')
        }
    }

    return (
        <form
            onSubmit={handleSubmit(submit, onInvalid)}
            onFocus={onFocus}
            className="flex flex-col gap-[22px]"
            noValidate
        >
            {form.fields.map((field, index) => (
                <Field
                    key={field.name ?? `${field.type}-${index}`}
                    field={field}
                    register={register}
                    error={field.name === undefined ? undefined : errors[field.name]?.message}
                />
            ))}

            {/* The honeypot the CMS asked for: hidden from people, filled in by
             * the sort of thing that fills in every field it finds. */}
            {form.honeypot === null ? null : (
                <input
                    type="text"
                    name={form.honeypot}
                    onChange={(event) => {
                        honeypotValue.current = event.target.value
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                />
            )}

            <div className="flex flex-wrap items-center gap-[18px]">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="cursor-pointer rounded-full border-0 bg-button px-7 py-3.5 font-sans text-[15px] font-medium text-button-text transition duration-200 ease-design hover:-translate-y-0.5 hover:opacity-80 disabled:cursor-wait disabled:opacity-60"
                >
                    {form.submit_label}
                </button>
                <span role="status" className={`font-mono text-xs ${failed ? 'text-accent' : 'text-dim'}`}>
                    {status}
                </span>
            </div>
        </form>
    )
}

/** The design's input: a rule under the answer, accented while it is being written. */
const INPUT =
    'border-0 border-b border-hair bg-transparent py-2.5 text-base text-fg transition-colors duration-200 outline-none focus:border-b-accent'

/** The type attribute an input takes, for the field types that are one. */
const INPUT_TYPES: Record<string, string> = {
    email: 'email',
    phone: 'tel',
    number: 'number',
    date: 'date',
}

type Register = UseFormRegister<Record<string, unknown>>

/**
 * One field of the form the CMS published, in whatever shape it published.
 *
 * The panel's palette is wider than a text box: a form can carry a heading, a
 * rule, a hidden value, a date, a set of options, and the consent box a Dutch
 * form is not allowed to do without. `buildFormSchema` has always validated
 * all of them — this renders them, so a field added in the panel arrives as
 * the control it was chosen to be rather than as a text input that stores the
 * word "true".
 *
 * An unknown type is a text input on purpose: a form published after a field
 * type ships still has to render, and the API decides what its answer means.
 */
function Field({ field, register, error }: { field: FormField; register: Register; error?: unknown }) {
    // Layout fields hold nothing, which is why the payload gives them no name.
    if (field.type === 'heading') {
        return <h3 className="m-0 mt-2 text-[19px] tracking-[-0.02em]">{field.text}</h3>
    }

    if (field.type === 'paragraph') {
        return (
            <p className="m-0 max-w-[62ch] text-[14.5px] leading-relaxed text-pretty text-dim">
                {withLinks(field.text ?? '')}
            </p>
        )
    }

    if (field.type === 'divider') {
        return <hr className="my-1 border-0 border-t border-hair" />
    }

    if (field.name === undefined || field.name === '') {
        return null
    }

    const name = field.name
    const label = field.label ?? name

    // Posted back unchanged, and never shown — a hidden field an editor can
    // see is not hidden.
    if (field.type === 'hidden') {
        return <input type="hidden" {...register(name)} />
    }

    // Consent and a single checkbox are the same control; what differs is that
    // consent cannot be skipped, which the schema already enforces.
    if (field.type === 'checkbox' || field.type === 'consent') {
        const wording = <span className="text-fg">{withLinks(label)}</span>

        return (
            <div className="flex flex-col gap-[9px]">
                {hasLink(label) ? (
                    // A consent box that names the privacy policy has to let
                    // the visitor read it, and a link inside a `<label>` ticks
                    // the box on the way — so the words stop being the label
                    // and start being what the label points at. The checkbox
                    // keeps its name; clicking the sentence no longer toggles
                    // it, which is the price of the sentence being clickable.
                    <div className="flex items-start gap-3 text-[14.5px] leading-relaxed text-dim">
                        <input
                            type="checkbox"
                            {...register(name)}
                            aria-labelledby={`${name}-label`}
                            className="mt-[3px] cursor-pointer accent-accent"
                        />
                        <span id={`${name}-label`}>{wording}</span>
                    </div>
                ) : (
                    <label className="flex cursor-pointer items-start gap-3 text-[14.5px] leading-relaxed text-dim">
                        <input
                            type="checkbox"
                            {...register(name)}
                            className="mt-[3px] cursor-pointer accent-accent"
                        />
                        {wording}
                    </label>
                )}
                <Help description={field.description} />
                <Error message={error} />
            </div>
        )
    }

    if (field.type === 'radio' || field.type === 'checkboxes') {
        const single = field.type === 'radio'

        return (
            <fieldset className="m-0 flex flex-col gap-[9px] border-0 p-0">
                <legend className="p-0 font-mono text-[10.5px] tracking-[0.08em] text-faint uppercase">
                    {label}
                </legend>
                <Help description={field.description} />
                <div className="flex flex-col gap-2">
                    {(field.options ?? []).map((option) => (
                        <label
                            key={option.value}
                            className="flex cursor-pointer items-center gap-3 text-[15px] text-dim"
                        >
                            <input
                                type={single ? 'radio' : 'checkbox'}
                                value={option.value}
                                {...register(name)}
                                className="cursor-pointer accent-accent"
                            />
                            <span className="text-fg">{option.label}</span>
                        </label>
                    ))}
                </div>
                <Error message={error} />
            </fieldset>
        )
    }

    return (
        <label className="flex flex-col gap-[9px]">
            <span className="font-mono text-[10.5px] tracking-[0.08em] text-faint uppercase">{label}</span>
            <Help description={field.description} />

            {field.type === 'textarea' ? (
                <textarea
                    {...register(name)}
                    rows={4}
                    placeholder={field.placeholder}
                    className={`resize-y ${INPUT}`}
                />
            ) : field.type === 'select' ? (
                // The empty first option is what keeps "not answered" an
                // answer: without it the first choice is selected for the
                // visitor and a required question is never asked.
                <select {...register(name)} className={`cursor-pointer ${INPUT}`}>
                    <option value="">{field.placeholder ?? '—'}</option>
                    {(field.options ?? []).map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    {...register(name)}
                    type={INPUT_TYPES[field.type] ?? 'text'}
                    placeholder={field.placeholder}
                    min={field.type === 'number' && field.min !== null ? field.min : undefined}
                    max={field.type === 'number' && field.max !== null ? field.max : undefined}
                    className={INPUT}
                />
            )}

            <Error message={error} />
        </label>
    )
}

/** An editor's help text. Empty means "not set" everywhere in this payload. */
function Help({ description }: { description?: string }) {
    if (description === undefined || description === '') {
        return null
    }

    return <span className="font-mono text-[11px] leading-relaxed text-faint">{withLinks(description)}</span>
}

/** Laravel's own wording, whether it was caught here or one round trip later. */
function Error({ message }: { message?: unknown }) {
    if (typeof message !== 'string') {
        return null
    }

    return <span className="font-mono text-[11px] text-accent">{message}</span>
}
